import type { MapRect } from './control-zones';
import { MARKER_PIN_SIZE_PX } from './markers';
import type { PixelPoint } from './types';

export type PanelSide = 'right' | 'left' | 'bottom' | 'top';

/** Where the panel's top-left corner goes, in map-area pixels, and which side it landed on. */
export type PanelPlacement = { side: PanelSide; x: number; y: number };

/** Between the anchor and the panel — the `--space-3` token, in the pixels this maths needs. */
export const PANEL_GAP = 12;

/**
 * A Device marker's projected box — the anchor the quick view is placed against (113588 §2a, §2b).
 *
 * A rect, not the point, because "does not cover the marker itself" is only expressible against an
 * area. The size is the **drawn** pin (`MARKER_PIN_SIZE_PX`), not the larger hit target: the panel
 * must clear what the Grower can see, and 8px of invisible slop is not that.
 *
 * A **cluster marker** is drawn at the same 40px, so §2g re-anchors through this same function with
 * the cluster's centre — one rule, no second literal.
 */
export function markerAnchorRect(point: PixelPoint): MapRect {
  const half = MARKER_PIN_SIZE_PX / 2;
  return {
    x: point.x - half,
    y: point.y - half,
    width: MARKER_PIN_SIZE_PX,
    height: MARKER_PIN_SIZE_PX,
  };
}

/**
 * Tried in this order. Right first because both details panels read left-to-right beside the thing
 * they describe; the order is fixed rather than configurable so two callers cannot disagree about
 * where a panel "should" go.
 */
const SIDE_ORDER: readonly PanelSide[] = ['right', 'left', 'bottom', 'top'];

/**
 * Where a map-anchored panel sits beside its Field or Device marker. One function serves both panels,
 * which is what makes 113586 §4b–§4c and 113588's quick-view placement the same rule rather than two
 * implementations that drift.
 *
 * The anchor is a **rect** — a Field's projected bounding box, later a marker's pin bounds — not a
 * point, because "does not cover the Field itself" is only expressible against an area.
 *
 * A side is taken when the panel is fully inside the map area, clear of the anchor, and clear of every
 * control exclusion zone (§4f). Where no side satisfies all three — a Field larger than the visible
 * map has none — §4c wins over §4b (grilling Q25): the panel is clamped fully into the map area and
 * the least-overlapping candidate taken, controls weighed before the anchor, because a clipped panel
 * is unusable while a partly covered Field is merely suboptimal.
 *
 * `markerAnchorRect` boxes a Device marker's point into this same anchor shape (ticket 11).
 */
export function placeMapPanel(input: {
  anchor: MapRect;
  panel: { width: number; height: number };
  viewport: { width: number; height: number };
  /**
   * Required, not optional: §17a is unconditional, and a caller that forgot the argument would cover
   * the controls with nothing failing. Pass `controlExclusionZones(viewport)`; `[]` is a deliberate
   * statement that this map area has no controls to avoid.
   */
  exclusions: readonly MapRect[];
  /**
   * Forces the side, and with it **skips every correction** — no flip, no clamp, no exclusion
   * check. 113588 §2f: once the Grower has panned the panel to the edge it is "not re-flipped or
   * shifted to bring it back into view", so the caller that locked a side at open (§2c) hands it
   * back on every later frame. Absent ⇒ choose freely, which is what §2c does at open and §16b does
   * again on a resize.
   */
  side?: PanelSide;
}): PanelPlacement {
  const { anchor, panel, viewport, exclusions } = input;

  if (input.side) {
    const rect = candidateRect(input.side, anchor, panel, viewport, false);
    return { side: input.side, x: rect.x, y: rect.y };
  }

  const candidates = SIDE_ORDER.map((side) => ({
    side,
    rect: candidateRect(side, anchor, panel, viewport),
  }));

  const clear = candidates.find(
    ({ rect }) =>
      isInside(rect, viewport) &&
      overlapArea(rect, anchor) === 0 &&
      exclusions.every((zone) => overlapArea(rect, zone) === 0),
  );
  if (clear) return { side: clear.side, x: clear.rect.x, y: clear.rect.y };

  const best = candidates
    .map(({ side, rect }) => {
      const clamped = {
        ...rect,
        x: clamp(rect.x, 0, viewport.width - panel.width),
        y: clamp(rect.y, 0, viewport.height - panel.height),
      };
      return {
        side,
        rect: clamped,
        controls: exclusions.reduce((sum, zone) => sum + overlapArea(clamped, zone), 0),
        anchor: overlapArea(clamped, anchor),
      };
    })
    // Fewest controls covered first, then least of the anchor covered. A strict comparison keeps the
    // earlier side on a tie, so `SIDE_ORDER` decides and the answer is repeatable.
    .reduce((a, b) =>
      b.controls < a.controls || (b.controls === a.controls && b.anchor < a.anchor) ? b : a,
    );

  return { side: best.side, x: best.rect.x, y: best.rect.y };
}

/** Adjacent on the main axis, centred on the anchor across it, then clamped across it. */
function candidateRect(
  side: PanelSide,
  anchor: MapRect,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
  /** Centre-axis clamping. `false` for a forced side — see `placeMapPanel`'s `side` (§2f). */
  clampCross = true,
): MapRect {
  const across = (value: number, extent: number, size: number) =>
    clampCross ? clamp(value, 0, extent - size) : value;

  if (side === 'right' || side === 'left') {
    return {
      x:
        side === 'right' ? anchor.x + anchor.width + PANEL_GAP : anchor.x - PANEL_GAP - panel.width,
      y: across(anchor.y + anchor.height / 2 - panel.height / 2, viewport.height, panel.height),
      width: panel.width,
      height: panel.height,
    };
  }

  return {
    x: across(anchor.x + anchor.width / 2 - panel.width / 2, viewport.width, panel.width),
    y:
      side === 'bottom'
        ? anchor.y + anchor.height + PANEL_GAP
        : anchor.y - PANEL_GAP - panel.height,
    width: panel.width,
    height: panel.height,
  };
}

/** `Math.max(min, max)` so a panel taller or wider than the map area clamps to the origin. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function isInside(rect: MapRect, viewport: { width: number; height: number }): boolean {
  return (
    rect.x >= 0 &&
    rect.y >= 0 &&
    rect.x + rect.width <= viewport.width &&
    rect.y + rect.height <= viewport.height
  );
}

function overlapArea(a: MapRect, b: MapRect): number {
  const width = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));
  const height = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
  return width * height;
}
